import * as sdk from '@cypro/server/sdk'
import {OverdosePreventionLevel} from '@cypro/engine/database/schema'
import {type PatientTherapySequence} from '@cypro/engine/schema'
import {dialog} from '@cypro/ui/components'
import {toaster} from '@cypro/ui/toaster'
import {type TFnType} from '@tolgee/react'
import {OverdoseWarningDialog} from './overdose-warning-dialog'

type ReachedDose = NonNullable<sdk.PostPatientsCanAddTherapyResponses['200']['reachedDoses']>[number]

/**
 * Hard stops block; a soft warning asks the user to proceed. Uses the same dialog as the
 * prescription pop-up (including its fixed `overdose-warning` id).
 */
async function confirmOverdoseWarnings(reached: ReachedDose[]): Promise<boolean> {
  const hardStop = reached.find(dose => dose.overdosePreventionLevel === OverdosePreventionLevel.enum.HARD_STOP)
  const warning = reached.find(dose => dose.overdosePreventionLevel === OverdosePreventionLevel.enum.SOFT_WARNING)
  const dose = hardStop ?? warning
  if (!dose) return true

  let cancelled = false
  await dialog.open('overdose-warning', {
    children: (
      <OverdoseWarningDialog
        name={dose.substanceName}
        limit={dose.limit}
        unit={dose.unit}
        overdosePreventionLevel={dose.overdosePreventionLevel}
        onSubmit={() => {}}
        onCancel={() => {
          cancelled = true
        }}
      />
    ),
  })

  return !hardStop && !cancelled
}

/** The stored prescription checked against the patient's history (excluding itself). */
export async function checkSequenceOverdose(sequence: PatientTherapySequence, t: TFnType): Promise<boolean> {
  const {data} = await sdk.postPatientsCanAddTherapy({
    body: {
      id: sequence.id,
      patientId: sequence.patientId,
      patientProtocolId: sequence.patientProtocolId,
      fixedDate: sequence.therapyDate,
      day: sequence.day,
      medicine: (sequence.medicine ?? []).map(medicine => ({
        activeSubstanceId: medicine.activeSubstanceId,
        prescribedNameId: medicine.prescribedNameId,
        calculationMethod: medicine.calculationMethod,
        regularDose: medicine.regularDose,
        precisionDecimals: medicine.precisionDecimals,
      })),
    },
  })

  if (data?.errorMessage) {
    toaster.error({title: t(data.errorMessage)})
    return false
  }

  return confirmOverdoseWarnings(data?.reachedDoses ?? [])
}
